import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return message.warning('Completa todos los campos');
    setLoading(true);
    try {
      await login(email, password);
      message.success('Bienvenido a Swomey');
      navigate('/');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #1F2F3F 0%, #2d4356 50%, #1F2F3F 100%)',
    }}>
      {/* Left panel — branding */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 48,
        color: '#fff',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 56, color: '#cfb56b', marginBottom: 8 }}>
            Swomey
          </h1>
          <p style={{ fontSize: 18, opacity: 0.8, maxWidth: 400 }}>
            Consultoría Inmobiliaria en Mallorca
          </p>
          <div style={{ marginTop: 48, display: 'flex', gap: 32, justifyContent: 'center' }}>
            {[
              { num: '89+', label: 'Operaciones' },
              { num: '15+', label: 'Años' },
              { num: '100%', label: 'Seguimiento' },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#cfb56b' }}>{s.num}</div>
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        width: 480,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        borderRadius: '24px 0 0 24px',
      }}>
        <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 360, padding: 48 }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: '#1F2F3F', marginBottom: 8 }}>
            Iniciar sesión
          </h2>
          <p style={{ color: '#888', marginBottom: 32, fontSize: 14 }}>
            Accede a tu panel de gestión inmobiliaria
          </p>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              style={{
                width: '100%', padding: '10px 14px', border: '1px solid #ddd',
                borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>
              Contraseña
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                style={{
                  width: '100%', padding: '10px 40px 10px 14px', border: '1px solid #ddd',
                  borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#999',
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px 24px', background: '#1F2F3F', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.2s',
            }}
          >
            {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {loading ? 'Accediendo...' : 'Acceder'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#bbb' }}>
            Swomey CRM © 2026
          </p>
        </form>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus { border-color: #1F2F3F !important; box-shadow: 0 0 0 2px rgba(31,47,63,0.1); }
        button[type="submit"]:hover:not(:disabled) { filter: brightness(1.15); }
      `}</style>
    </div>
  );
}
